import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import {
  PhCaretDown,
  PhCaretUp,
  PhCheck,
  PhClock,
  PhCurrencyCircleDollar,
  PhInfo,
  PhThumbsUp,
  PhWarningCircle,
  PhXCircle,
} from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import type { ChipIntent } from 'tio-ui/components/chip';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import type { AppHistoryBase } from '../../types/tuition-assistance.ts';
import type IntlService from 'ember-intl/services/intl';
import { formatAsDollars } from '../../utils/format.ts';

type EventCategory = 'rejection' | 'approval' | 'submission' | 'info' | 'payment' | 'neutral';

interface TimelineComment {
  note: string;
  /** Everyone who left this exact note, most recent first */
  authorLabel: string;
  /** Shown only when every author acted with the same role */
  role?: string;
  /** Timestamp of the most recent occurrence of this note */
  date: string;
}

interface TimelineEvent {
  category: EventCategory;
  label: string;
  date: string;
  comments: TimelineComment[];
  chipLabel?: string;
  chipIntent?: ChipIntent;
  isLast: boolean;
}

interface StepNote {
  note: string;
  name: string;
  role?: string;
  date: string;
}

/** A step note tied to the timeline entry it was left on */
interface PlacedStepNote extends StepNote {
  event: { comments: TimelineComment[] };
}

interface ApplicationHistoryTimelineSignature {
  Args: {
    history: AppHistoryBase[];
    title?: string;
    translationPrefix?: string;
    /** When true, hides the header/border — for use inside a drawer or other container */
    embedded?: boolean;
  };
  Element: HTMLDivElement;
}

// Maps a state transition type to an event category
function categorizeStateTransition(type?: string): EventCategory {
  if (!type) return 'neutral';
  if (type.includes('NOT_APPROVED') || type.includes('REJECTED')) return 'rejection';
  if (type.includes('APPROVED') || type === 'FULFILLED') return 'approval';
  if (type.includes('PENDING') || type === 'COURSES_IN_PROGRESS') return 'submission';
  if (type === 'INFO_REQUESTED') return 'info';
  if (type === 'ABANDONED') return 'neutral';
  return 'neutral';
}

// Assistance types that represent actual money movement
const PAYMENT_ASSISTANCE_TYPES = new Set([
  'PAYMENT',
  'PAYMENT_PENDING',
  'PAYMENT_AMOUNT_UPDATED',
  'CARD_AUTHORIZED',
  'CARD_CAPTURED',
  'REPAYMENT_REQUEST',
  'REPAYMENT',
  'REPAYMENT_FORGIVEN',
  'REPAYMENT_CANCELLED',
  'REPAYMENT_AMOUNT_UPDATED',
]);

// Assistance types that are informational (card status, not actual payments)
const INFO_ASSISTANCE_TYPES = new Set([
  'CARD_RESERVATION_CREATED',
  'CARD_RESERVATION_AMOUNT_UPDATED',
  'CARD_EXPIRED',
  'CARD_REFUNDED',
  'CARD_DECLINED',
  'CARD_DISPUTED',
]);

function categorizeAssistance(assistanceType?: string): EventCategory {
  if (!assistanceType) return 'neutral';
  if (PAYMENT_ASSISTANCE_TYPES.has(assistanceType)) return 'payment';
  if (INFO_ASSISTANCE_TYPES.has(assistanceType)) return 'info';
  return 'neutral';
}

// Filter out internal system strings that shouldn't be shown to users
function sanitizeComment(comment?: string): string | undefined {
  const trimmed = comment?.trim();
  if (!trimmed) return undefined;
  if (trimmed.includes('.state_') || trimmed.includes('::')) {
    return undefined;
  }
  // Filter Ruby class-name strings (e.g. "TasAssistance::Foo") but not
  // legitimate comments that happen to start with a proper noun.
  if (/^[A-Z][a-z]+[A-Z]/.test(trimmed) && !/\s/.test(trimmed)) {
    return undefined;
  }
  return trimmed;
}

// The transition types that make up one approval step. Notes deduplicate
// within one review cycle of a step: a submission opens a cycle, the system's
// actor-less re-requests between chained approvers continue it, and a
// rejection (or any transition outside the step) ends it.
const APPROVAL_STEP_BY_TRANSITION_TYPE: Record<string, string> = {
  PENDING_COURSES_APPROVAL: 'courses',
  COURSES_APPROVED: 'courses',
  COURSES_NOT_APPROVED: 'courses',
  COURSES_REJECTED: 'courses',
  PENDING_EVIDENCE_APPROVAL: 'evidence',
  EVIDENCE_APPROVED: 'evidence',
  EVIDENCE_NOT_APPROVED: 'evidence',
  EVIDENCE_REJECTED: 'evidence',
  // Program-instance histories flow through this component too
  PENDING_PROGRAM_APPROVAL: 'program',
  PROGRAM_APPROVED: 'program',
  PROGRAM_NOT_APPROVED: 'program',
  PROGRAM_REJECTED: 'program',
  PENDING_COMPLETION_APPROVAL: 'completion',
  COMPLETION_APPROVED: 'completion',
  COMPLETION_NOT_APPROVED: 'completion',
  COMPLETION_REJECTED: 'completion',
  PENDING_WITHDRAWAL_APPROVAL: 'withdrawal',
  WITHDRAWAL_APPROVED: 'withdrawal',
  WITHDRAWAL_NOT_APPROVED: 'withdrawal',
  WITHDRAWAL_REJECTED: 'withdrawal',
};

const byDateDesc = (a: { date: string }, b: { date: string }) =>
  new Date(b.date).getTime() - new Date(a.date).getTime();

// Turn a review cycle's notes into display cards, each on the entry it was
// left on. Identical notes merge into one card with the authors combined,
// placed on the note's most recent occurrence — so a multi-approver chain
// doesn't repeat the same boilerplate.
function placeChainComments(notes: PlacedStepNote[]): void {
  const byText = new Map<string, PlacedStepNote[]>();
  for (const entry of notes) {
    const group = byText.get(entry.note);
    if (group) group.push(entry);
    else byText.set(entry.note, [entry]);
  }

  for (const group of byText.values()) {
    const sorted = [...group].sort(byDateDesc);
    const newest = sorted[0];
    if (!newest) continue;

    // Unique authors, most recent occurrence first
    const authors: StepNote[] = [];
    for (const entry of sorted) {
      if (!authors.some((author) => author.name === entry.name)) authors.push(entry);
    }
    const sharedRole = authors.every((author) => author.role === newest.role);

    newest.event.comments.push({
      note: newest.note,
      authorLabel: sharedRole
        ? authors.map((author) => author.name).join(', ')
        : authors
            .map((author) => (author.role ? `${author.name} (${author.role})` : author.name))
            .join(', '),
      role: sharedRole ? newest.role : undefined,
      date: newest.date,
    });
  }
}

function chipForCategory(
  category: EventCategory,
  stateTransitionType?: string
): { labelKey: string; intent: ChipIntent } | null {
  switch (category) {
    case 'rejection':
      return { labelKey: 'common.tas.timeline_chips.action_needed', intent: 'danger' };
    case 'approval':
      return { labelKey: 'common.tas.timeline_chips.approved', intent: 'success' };
    case 'submission':
      if (stateTransitionType === 'COURSES_IN_PROGRESS') {
        return { labelKey: 'common.tas.timeline_chips.in_progress', intent: 'primary' };
      }
      return { labelKey: 'common.tas.timeline_chips.submitted', intent: 'primary' };
    case 'info':
      return { labelKey: 'common.tas.timeline_chips.update', intent: 'warning' };
    case 'payment':
      return { labelKey: 'common.tas.timeline_chips.payment', intent: 'primary' };
    default:
      return null;
  }
}

const DOT_CLASSES: Record<EventCategory, string> = {
  rejection: 'app-timeline-dot--danger',
  approval: 'app-timeline-dot--success',
  submission: 'app-timeline-dot--info',
  info: 'app-timeline-dot--warning',
  payment: 'app-timeline-dot--info',
  neutral: 'app-timeline-dot--neutral',
};

// Category → left-border accent on the comment card (composes with
// `border-l-4`). Routes through the `.app-timeline-rail--{intent}` helpers.
const COMMENT_BORDER_CLASSES: Record<EventCategory, string> = {
  rejection: 'app-timeline-rail--danger',
  approval: 'app-timeline-rail--success',
  submission: 'app-timeline-rail--info',
  info: 'app-timeline-rail--warning',
  payment: 'app-timeline-rail--info',
  neutral: 'app-timeline-rail--neutral',
};

class ApplicationHistoryTimeline extends Component<ApplicationHistoryTimelineSignature> {
  @service declare intl: IntlService;
  @tracked isExpanded = true;

  get prefix() {
    return this.args.translationPrefix || 'tuition_assistance.applications.history';
  }

  /**
   * The capacity the actor acted in ('Supervisor', 'Tuition.io Approver', …)
   * via the shared user-roles translations; falls back to the raw platform
   * role (e.g. 'EMPLOYEE') for older history entries without one.
   */
  #displayRole(item: AppHistoryBase): string | undefined {
    const { relationshipType } = item;
    if (relationshipType) {
      const key = `user-roles.${relationshipType.toLowerCase()}`;
      if (this.intl.exists(key)) return this.intl.t(key);
    }
    return item.role;
  }

  toggle = () => {
    this.isExpanded = !this.isExpanded;
  };

  /**
   * One timeline entry per history record, most recent first. Each comment
   * renders on the entry it was left on. Within one review cycle (a
   * submission and the approver chain it kicks off), identical notes merge
   * into a single card — with the authors combined, on the note's most
   * recent occurrence — so multi-approver chains don't repeat the same
   * boilerplate. Rejections and new submissions end the cycle, so notes
   * never merge across review cycles.
   */
  get timelineEvents(): TimelineEvent[] {
    const ascending = this.args.history
      .filter(
        (item) =>
          item.logType !== 'state_transition' ||
          (item as { stateTransitionType?: string }).stateTransitionType !== 'CREATED'
      )
      .slice()
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    type PendingEvent = Omit<TimelineEvent, 'isLast'>;

    let currentChain: string | undefined;
    let chainNotes: PlacedStepNote[] = [];

    const closeChain = () => {
      placeChainComments(chainNotes);
      currentChain = undefined;
      chainNotes = [];
    };

    const events = ascending.map((item): PendingEvent => {
      const role = this.#displayRole(item);
      const byApprover = item.approverName
        ? ' ' + this.intl.t('common.tas.timeline_by', { name: item.approverName })
        : '';
      const formattedName = byApprover && role ? `${byApprover} - ${role}` : byApprover;

      let category: EventCategory;
      let label: string;

      switch (item.logType) {
        case 'assistance': {
          category = categorizeAssistance(item.assistanceType);
          label = this.intl.t(`${this.prefix}.${item.assistanceType}`, {
            amount: formatAsDollars(Number(item.amount) || 0),
            name: byApprover,
          });
          break;
        }
        case 'approval': {
          category = 'approval';
          const updateType =
            item.from === item.to ? 'approved_total_set' : 'approved_total_updated';
          label = this.intl.t(`${this.prefix}.${updateType}`, {
            amount: formatAsDollars(Number(item.to) || 0),
            from: formatAsDollars(Number(item.from) || 0),
            to: formatAsDollars(Number(item.to) || 0),
            name: byApprover,
          });
          break;
        }
        case 'state_transition':
        default: {
          category = categorizeStateTransition(item.stateTransitionType);
          label = this.intl.t(`${this.prefix}.${item.stateTransitionType}`, {
            name: formattedName,
          });
          break;
        }
      }

      const chip = chipForCategory(
        category,
        item.logType === 'state_transition' ? item.stateTransitionType : undefined
      );

      const event: PendingEvent = {
        category,
        label,
        date: item.date,
        comments: [],
        chipLabel: chip ? this.intl.t(chip.labelKey) : undefined,
        chipIntent: chip?.intent,
      };

      const note = sanitizeComment(item.approverComment);
      const stepNote: PlacedStepNote | undefined = note
        ? { note, name: item.approverName || 'System', role, date: item.date, event }
        : undefined;

      const isStateTransition = !item.logType || item.logType === 'state_transition';
      const step = isStateTransition
        ? APPROVAL_STEP_BY_TRANSITION_TYPE[item.stateTransitionType ?? '']
        : undefined;

      if (step) {
        // A request with an actor is an employee (re)submission opening a new
        // review cycle; the system's actor-less re-requests between chained
        // approvers continue the current one.
        const opensCycle = category === 'submission' && !!item.approverName;
        if (step !== currentChain || opensCycle) closeChain();
        currentChain = step;
        if (stepNote) chainNotes.push(stepNote);
        // A rejection ends the cycle: its reason stays on this entry, and the
        // next cycle's notes must not merge with this one's.
        if (category === 'rejection') closeChain();
      } else {
        // Assistance/approved-total records interleave with a cycle's
        // transitions without ending it; other state transitions end it.
        if (isStateTransition) closeChain();
        if (stepNote) placeChainComments([stepNote]);
      }

      return event;
    });
    closeChain();

    return events
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((event, idx, arr) => ({ ...event, isLast: idx === arr.length - 1 }));
  }

  <template>
    <div
      class={{unless @embedded "rounded-xl border tio-border-default tio-bg-surface"}}
      ...attributes
    >
      {{#unless @embedded}}
        <button
          type="button"
          class="tio-row-between w-full px-5 py-4 text-left hover:tio-bg-sunken rounded-xl transition-colors"
          {{on "click" this.toggle}}
        >
          <div class="tio-row-flush gap-2.5">
            <PhClock @size={{18}} class="tio-fg-disabled" />
            <span class="tio-text-sm-semibold tio-fg-primary">
              {{if @title @title (t "tuition_assistance.history.title")}}
            </span>
            <span
              class="tio-inline-row-center rounded-full tio-bg-sunken px-2 py-0.5 tio-text-xs-medium tio-fg-secondary"
            >
              {{this.timelineEvents.length}}
            </span>
          </div>
          {{#if this.isExpanded}}
            <PhCaretUp @size={{16}} class="tio-fg-disabled" />
          {{else}}
            <PhCaretDown @size={{16}} class="tio-fg-disabled" />
          {{/if}}
        </button>
      {{/unless}}

      {{#if (if @embedded true this.isExpanded)}}
        <div class={{unless @embedded "px-5 pb-5"}}>
          <div class="relative">
            {{#each this.timelineEvents as |event|}}
              <div
                class="relative flex gap-4 {{unless event.isLast 'pb-6'}}"
                data-test-app-timeline-entry
              >
                {{#unless event.isLast}}
                  <div class="absolute left-3 top-6 bottom-0 w-px tio-bg-divider"></div>
                {{/unless}}

                <div class="relative z-10 shrink-0 mt-0.5">
                  <div
                    class="app-timeline-dot tio-row-center h-6 w-6 rounded-full
                      {{this.dotClass event.category}}"
                  >
                    {{#if (eq event.category "rejection")}}
                      <PhXCircle @size={{14}} class="tio-fg-inverse" />
                    {{else if (eq event.category "approval")}}
                      <PhCheck @size={{14}} class="tio-fg-inverse" />
                    {{else if (eq event.category "submission")}}
                      <PhThumbsUp @size={{14}} class="tio-fg-inverse" />
                    {{else if (eq event.category "info")}}
                      <PhInfo @size={{14}} class="tio-fg-inverse" />
                    {{else if (eq event.category "payment")}}
                      <PhCurrencyCircleDollar @size={{14}} class="tio-fg-inverse" />
                    {{else}}
                      <PhWarningCircle @size={{14}} class="tio-fg-inverse" />
                    {{/if}}
                  </div>
                </div>

                <div class="tio-fill-min pt-0.5">
                  <div class="flex items-start justify-between gap-x-3">
                    <p class="tio-text-sm-medium tio-fg-primary min-w-0">
                      {{event.label}}
                    </p>
                    <div class="tio-stack-end shrink-0 gap-1">
                      <time datetime={{event.date}} class="text-xs text-right whitespace-nowrap">
                        <span class="tio-fg-disabled">
                          {{safeFormatDate event.date month="short" day="2-digit" year="numeric"}}
                        </span>
                        <span class="px-1 tio-fg-faint">|</span>
                        <span class="font-medium tio-fg-secondary">
                          {{safeFormatDate
                            event.date
                            hour="numeric"
                            minute="2-digit"
                            hour12=true
                            timeZoneName="short"
                          }}
                        </span>
                      </time>
                      {{#if event.chipLabel}}
                        <Chip @intent={{event.chipIntent}} @size="sm" @appearance="faded">
                          {{event.chipLabel}}
                        </Chip>
                      {{/if}}
                    </div>
                  </div>

                  {{! Approver comment cards — each comment renders on the
                      entry it was left on; identical notes from one review
                      cycle merge into a single card with the authors combined }}
                  {{#each event.comments as |comment|}}
                    <div
                      class="app-timeline-comment mt-2 border border-l-4
                        {{this.commentBorderClass event.category}}
                        tio-surface-sunken p-3"
                      data-test-app-timeline-comment
                    >
                      <div class="flex items-start justify-between gap-x-3 mb-1">
                        <div class="tio-row-snug min-w-0">
                          <span class="tio-text-xs-medium tio-fg-secondary">
                            {{comment.authorLabel}}
                          </span>
                          {{#if comment.role}}
                            <span
                              class="text-xs tio-fg-disabled before:content-['·'] before:mr-1.5"
                            >{{comment.role}}</span>
                          {{/if}}
                        </div>
                        <time
                          datetime={{comment.date}}
                          class="text-xs tio-fg-disabled text-right whitespace-nowrap shrink-0"
                        >
                          {{safeFormatDate comment.date month="short" day="2-digit" year="numeric"}}
                          <span class="tio-fg-faint">|</span>
                          {{safeFormatDate
                            comment.date
                            hour="numeric"
                            minute="2-digit"
                            hour12=true
                          }}
                        </time>
                      </div>
                      <p class="text-sm leading-relaxed tio-fg-secondary">
                        {{comment.note}}
                      </p>
                    </div>
                  {{/each}}
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>

  dotClass = (category: EventCategory) => DOT_CLASSES[category] ?? DOT_CLASSES.neutral;
  commentBorderClass = (category: EventCategory) =>
    COMMENT_BORDER_CLASSES[category] ?? COMMENT_BORDER_CLASSES.neutral;
}

export { type ApplicationHistoryTimelineSignature };
export default ApplicationHistoryTimeline;
